import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeftOutlined,
  WarningOutlined,
  LockOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  UploadOutlined,
  DownloadOutlined,
  SafetyCertificateOutlined
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Modal,
  Radio,
  Select,
  Table,
  Alert,
  Upload,
  notification,
  message,
  Popconfirm,
  Empty,
  Tooltip,
} from "antd";
import { BsThreeDotsVertical } from "react-icons/bs";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import creditIcon from "../../../../Assets/Icons/garage_money.svg";
import paymentIcon from "../../../../Assets/Icons/payments.svg";
import grayPaymentIcon from "../../../../Assets/Icons/gray_payments.svg";
import humididtyIcon from "../../../../Assets/Icons/humidity_mid.svg";
import grayFuelIcon from "../../../../Assets/Icons/gray_fuel_icon.svg";
import dateIcon from "../../../../Assets/Icons/calendar_month.svg";
import regIcon from "../../../../Assets/Icons/markdown_copy.svg";
import currencyIcon from "../../../../Assets/Icons/attach_money.svg";
import contactIcon from "../../../../Assets/Icons/account_child_invert.svg";
import mailIcon from "../../../../Assets/Icons/mail.svg";
import locIcon from "../../../../Assets/Icons/distance.svg";
import callIcon from "../../../../Assets/Icons/call.svg";
import wipayAccountIcon from "../../../../Assets/Icons/wipay_account.svg";
import wipayApiIcon from "../../../../Assets/Icons/wipay_API.svg";
import vendorTypeIcon from "../../../../Assets/Icons/vendor_type.svg";
import pinDropIcon from "../../../../Assets/Icons/pin_drop.svg";
import storefrontIcon from "../../../../Assets/Icons/station_name.svg";
import paymentIconSale from "../../../../Assets/Icons/payments_sales.svg";
import settlementIcon from "../../../../Assets/Icons/laptop_car.svg";
import payableIcon from "../../../../Assets/Icons/payments.svg";
import netPayable from "../../../../Assets/Icons/payment_arrow_down.svg";
import GenericTable from "../../../../Components/Tables/GenericTable";
import docIcon from "../../../../Assets/Icons/document.svg";
import pdfIcon from "../../../../Assets/Icons/pdf.svg";
import GenericModal from "../../../../Components/Modals/GenericModal";

import "./style.css";
import BarChart from "../../../../Components/Charts/BarChart";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import FuelTypeCard from "../../../../Components/Cards/FuelTypeCard";
import GenericOperatingHours from "../../../../Components/Tables/GenericOperatingHourTables";
import {
  getVendorById,
  updateVendorDetails,
  payVendorSettlement,
  initiateWiPaySettlement,
  getPayableTransactions,
} from "../../../../API/Admin/FuelVendors";
import SettlementTransactionsTable from "../../../../Components/Tables/SettlementTransactionsTable";
import { savePaymentReturn } from "../../../../Utlis/PaymentReturn";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

/* ─── shared helpers ─── */
const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US");
};

const formatPhone = (phone) => {
  if (!phone) return "-";
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const fieldValue = (field) =>
  field && typeof field === "object" ? field.value : field;

const formatMoney = (val) => {
  const num = parseFloat(val);
  return Number.isNaN(num)
    ? "$0.00"
    : `$${num.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  const messages = Object.entries(data).flatMap(([field, errors]) => {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
  });

  return messages.length ? messages.join(" | ") : fallback;
};

const VendorCell = ({ name }) => (
  <span className="fuel-vendor__person-cell">
    <span
      className="fuel-vendor__vendor-circle"
      style={{ background: getAvatarColor(name) }}
    >
      {getInitials(name)}
    </span>
    {name}
  </span>
);

const DriverCell = ({ name }) => (
  <span className="fuel-vendor__person-cell">
    <span
      className="fuel-vendor__vendor-circle"
      style={{ background: getAvatarColor(name) }}
    >
      {getInitials(name)}
    </span>
    {name}
  </span>
);

/* ─── Pay Vendor Modal ─── */
const EMPTY_PAYABLE = { transactions: [], summary: {} };

const PayVendorModal = ({
  open,
  onClose,
  onConfirm,
  onWiPay,
  vendor,
  vendorId,
}) => {
  const [paymentType, setPaymentType] = useState("all");
  const [payable, setPayable] = useState(EMPTY_PAYABLE);
  const [payableLoading, setPayableLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [wipayLoading, setWipayLoading] = useState(false);

  const loadPayable = async () => {
    if (!vendorId) return;
    setPayableLoading(true);
    try {
      const res = await getPayableTransactions(vendorId);
      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data || {};
        const transactions = Array.isArray(data.transactions)
          ? data.transactions
          : [];
        setPayable({ transactions, summary: data.summary || {} });
        setSelectedIds((prev) =>
          prev.filter((id) =>
            transactions.some((txn) => txn.id === id && txn.is_payable),
          ),
        );
      } else {
        setPayable(EMPTY_PAYABLE);
        message.error(
          getErrorMessage(res?.data, "Failed to load payable transactions."),
        );
      }
    } catch (err) {
      setPayable(EMPTY_PAYABLE);
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to load payable transactions.",
        ),
      );
    } finally {
      setPayableLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPaymentType("all");
      setSelectedIds([]);
      setFileList([]);
      setPaymentMethod("cash");
      loadPayable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendorId]);

  const { transactions, summary } = payable;
  const feePercent = parseFloat(summary.fee_percent) || 0;
  const isCustom = paymentType === "custom";

  const selectedTotal = transactions
    .filter((txn) => selectedIds.includes(txn.id))
    .reduce((sum, txn) => sum + (parseFloat(txn.amount) || 0), 0);

  const payAmount = isCustom
    ? selectedTotal
    : parseFloat(summary.payable_amount) || 0;
  const payCount = isCustom
    ? selectedIds.length
    : Number(summary.payable_count) || 0;
  const feeAmount = (payAmount * feePercent) / 100;
  const netAmount = payAmount - feeAmount;
  const onHoldCount = Number(summary.on_hold_count) || 0;
  const canPay = payCount > 0 && payAmount > 0;

  const buildSelection = () =>
    isCustom ? { payAll: false, transactionIds: selectedIds } : { payAll: true };

  const validateSelection = () => {
    if (canPay) return true;
    notification.error({
      message: isCustom ? "No Transactions Selected" : "Nothing To Pay",
      description: isCustom
        ? "Select at least one transaction to pay."
        : "There are no unpaid approved transactions to pay for this vendor.",
      placement: "topRight",
    });
    return false;
  };

  const handleWiPay = async () => {
    if (!validateSelection()) return;

    setWipayLoading(true);
    const success = await onWiPay(buildSelection());
    setWipayLoading(false);
    if (success) onClose();
    else loadPayable();
  };

  const handleConfirm = async () => {
    if (!validateSelection()) return;

    if (!fileList || fileList.length === 0) {
      notification.error({
        message: "Upload Required",
        description: "Please upload transaction screenshot before submitting.",
        placement: "topRight",
      });
      return;
    }

    setSubmitting(true);
    const success = await onConfirm({
      ...buildSelection(),
      paymentMethod,
      file: fileList[0]?.originFileObj,
    });
    setSubmitting(false);
    if (success) onClose();
    else loadPayable();
  };

  const payableColumns = [
    {
      title: "Txn Id",
      dataIndex: "txn_id",
      key: "txn_id",
      render: (txnId, record) => (
        <div className="pay-vendor-modal__txn-cell">
          <span>{txnId || "-"}</span>
          <span className="pay-vendor-modal__txn-date">
            {[record.date, record.time].filter(Boolean).join(" ") || "-"}
          </span>
        </div>
      ),
    },
    {
      title: "Vehicle",
      key: "vehicle",
      render: (_, record) =>
        [record.vehicle_id, record.vehicle_name].filter(Boolean).join(" · ") ||
        "-",
    },
    {
      title: "Driver",
      dataIndex: "driver_name",
      key: "driver_name",
      render: (name) => name || "-",
    },
    {
      title: "Fuel Type",
      dataIndex: "fuel_type_name",
      key: "fuel_type_name",
      render: (type) => type || "-",
    },
    {
      title: "Volume",
      dataIndex: "volume",
      key: "volume",
      render: (volume) => (volume ? `${volume} L` : "-"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatMoney(amount),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) =>
        record.is_payable ? (
          <span className="pay-vendor-modal__status pay-vendor-modal__status--payable">
            Payable
          </span>
        ) : (
          <div className="pay-vendor-modal__hold">
            <span className="pay-vendor-modal__status pay-vendor-modal__status--hold">
              On Hold
            </span>
            {record.hold_message && (
              <span className="pay-vendor-modal__hold-message">
                {record.hold_message}
              </span>
            )}
          </div>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={860}
      className="pay-vendor-modal"
      centered
    >
      <div className="pay-vendor-modal__header">
        <div className="pay-vendor-modal__title">Pay Vendor</div>
        <div className="pay-vendor-modal__subtitle">
          Pay all unpaid transactions or pick the ones to pay
        </div>
      </div>

      <div className="pay-vendor-modal__vendor-card">
        <div className="pay-vendor-modal__vendor-info">
          <div
            className="pay-vendor-modal__vendor-avatar"
            style={{ background: getAvatarColor(vendor?.company) }}
          >
            {getInitials(vendor?.company)}
          </div>
          <div>
            <div className="pay-vendor-modal__vendor-name">
              {vendor?.company || "-"}
            </div>
            <div className="pay-vendor-modal__vendor-id">
              VENDOR ID: {vendor?.id ?? "-"}
            </div>
          </div>
        </div>
        <div className="pay-vendor-modal__due">
          <div className="pay-vendor-modal__due-label">Outstanding Due</div>
          <div className="pay-vendor-modal__due-amount">
            {formatMoney(summary.due_amount)}
          </div>
        </div>
      </div>

      <Radio.Group
        className="pay-vendor-modal__radio-group"
        value={paymentType}
        onChange={(e) => setPaymentType(e.target.value)}
      >
        <Radio value="all">
          Pay All ({formatMoney(summary.payable_amount)} ·{" "}
          {Number(summary.payable_count) || 0} txn)
        </Radio>
        <Radio value="custom">Custom</Radio>
      </Radio.Group>

      {onHoldCount > 0 && (
        <div className="pay-vendor-modal__field">
          <Alert
            type="warning"
            showIcon
            message={`${onHoldCount} transaction${onHoldCount === 1 ? "" : "s"} (${formatMoney(summary.on_hold_amount)}) on hold and can't be paid yet.`}
          />
        </div>
      )}

      <div className="pay-vendor-modal__field">
        <label className="pay-vendor-modal__label">
          {isCustom ? "Select Transactions" : "Transactions"}
        </label>
        <Table
          size="small"
          rowKey="id"
          className="pay-vendor-modal__table"
          columns={payableColumns}
          dataSource={transactions}
          loading={payableLoading}
          pagination={{ pageSize: 5, hideOnSinglePage: true }}
          scroll={{ x: "max-content" }}
          rowClassName={(record) =>
            record.is_payable ? "" : "pay-vendor-modal__row--hold"
          }
          rowSelection={
            isCustom
              ? {
                  selectedRowKeys: selectedIds,
                  onChange: (keys) => setSelectedIds(keys),
                  getCheckboxProps: (record) => ({
                    disabled: !record.is_payable,
                  }),
                }
              : undefined
          }
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No unpaid transactions"
              />
            ),
          }}
        />
      </div>

      <div className="pay-vendor-modal__field">
        <div className="pay-vendor-modal__totals">
          <div className="pay-vendor-modal__total-row">
            <span>
              Amount ({payCount} transaction{payCount === 1 ? "" : "s"})
            </span>
            <span>{formatMoney(payAmount)}</span>
          </div>
          <div className="pay-vendor-modal__total-row">
            <span>Fee ({feePercent}%)</span>
            <span>-{formatMoney(feeAmount)}</span>
          </div>
          <div className="pay-vendor-modal__total-row pay-vendor-modal__total-row--net">
            <span>Net Paid</span>
            <span>{formatMoney(netAmount)}</span>
          </div>
        </div>
      </div>

      <div className="pay-vendor-modal__field">
        <label className="pay-vendor-modal__label">
          Payment Method <span className="pay-vendor-modal__required">*</span>
        </label>
        <Select
          className="pay-vendor-modal__select"
          value={paymentMethod}
          onChange={setPaymentMethod}
          options={[
            { value: "cash", label: "Cash" },
            { value: "bank", label: "Bank Transfer" },
          ]}
        />
      </div>

      <div className="pay-vendor-modal__field">
        <label className="pay-vendor-modal__label">
          Upload Transaction Screenshot{" "}
          <span className="pay-vendor-modal__required">*</span>
        </label>
        <Upload.Dragger
          fileList={fileList}
          onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
          beforeUpload={() => false}
          accept=".png,.jpg,.jpeg,.pdf"
          className="pay-vendor-modal__dragger"
        >
          <p className="pay-vendor-modal__dragger-text">
            Choose a file or drag & drop it here
          </p>
          <p className="pay-vendor-modal__dragger-hint">
            PNG, PDF and JPG formats, up to 5MB
          </p>
          <Button
            icon={<UploadOutlined />}
            size="small"
            className="pay-vendor-modal__browse-btn"
          >
            Browse File
          </Button>
        </Upload.Dragger>
      </div>

      <div className="pay-vendor-modal__footer">
        <Button
          className="pay-vendor-modal__wipay-btn"
          onClick={handleWiPay}
          loading={wipayLoading}
          disabled={!canPay || submitting}
        >
          Pay through WiPay
        </Button>
        <Button
          type="primary"
          className="pay-vendor-modal__confirm-btn"
          onClick={handleConfirm}
          loading={submitting}
          disabled={!canPay || wipayLoading}
        >
          Confirm Payment
        </Button>
      </div>
    </Modal>
  );
};

/* ─── Main Component ─── */
const FuelVendorDetails = () => {
  const { canEdit } = usePageAccess("fuel-vendors");
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [reason, setReason] = useState("");
  const [fuelTypes, setFuelTypes] = useState([]);
  const [fuelingTxnData, setFuelingTxnData] = useState([]);
  const [fuelDispensedByFuelType, setFuelDispensedByFuelType] = useState({
    labels: [],
    total_volume: [],
    total_amount: [],
  });
  const [vendorPaymentData, setVendorPaymentData] = useState([]);
  const [operatingHours, setOperatingHours] = useState([]);
  const [vendor, setVendor] = useState({
    id: "-",
    company: "",
    brandName: "-",
    fuelBrand: "-",
    avatar: null,
    dateIncorporated: "-",
    registrationDate: "-",
    taxId: "-",
    currency: "-",
    contactManager: "-",
    contactEmail: "-",
    contactPhone: "-",
    vendorType: "-",
    location: "-",
    stationName: "-",
    stationId: "-",
    stationAddress: "-",
    wipayAccountNumber: "-",
    wipayApiKey: "-",
    outstandingBalance: "$0.00",
    grossSales: "$0.00",
    feesSettlements: "$0.00",
    netPayable: "$0.00",
    totalRevenueYtd: "$0.00",
    avgTransactionAmount: "$0.00",
    totalSalesMtd: "$0.00",
    litersDispensed: "0",
    avgTransactionVolume: "0",
  });
  const companyName = vendor.company;

  const fetchVendor = async () => {
    if (!passedId) return;

    try {
      const res = await getVendorById(passedId);
      const data = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && data) {
        setVendor({
          id: data.id ?? "-",
          company: data.legal_name || data.brand_name || "-",
          brandName: data.brand_name || "-",
          fuelBrand: data.fuel_brand?.name ?? data.fuel_brand ?? "-",
          avatar: resolveFileUrl(data.avatar),
          dateIncorporated: formatDate(data.date_incorporated),
          registrationDate: data.registration_date
            ? formatDate(data.registration_date)
            : "Pending Approval",
          taxId: data.tax_id || "-",
          currency: data.currency || "-",
          contactManager: data.primary_contact_person || "-",
          contactEmail: data.contact_email || "-",
          contactPhone: formatPhone(data.contact_phone),
          vendorType: data.vendor_type || "-",
          location: data.address || "-",
          stationName: data.station_name || "-",
          stationId: data.station_id || "-",
          stationAddress: data.station_address || "-",
          wipayAccountNumber: data.wipay_account_number || "-",
          wipayApiKey: data.wipay_api_key || "-",
          outstandingBalance: formatMoney(data.outstanding_balance),
          grossSales: formatMoney(fieldValue(data.gross_sales)),
          grossSalesRaw: data.gross_sales,
          feesSettlements: formatMoney(data.fees_settlements),
          netPayable: formatMoney(fieldValue(data.net_payable)),
          netPayableRaw: data.net_payable,
          totalRevenueYtd: formatMoney(fieldValue(data.total_revenue_ytd)),
          totalRevenueYtdRaw: data.total_revenue_ytd,
          avgTransactionAmount: formatMoney(data.avg_transaction_amount),
          totalSalesMtd: formatMoney(fieldValue(data.total_sales_mtd)),
          totalSalesMtdRaw: data.total_sales_mtd,
          litersDispensed: `${data.liters_dispensed ?? 0} L`,
          avgTransactionVolume: `${fieldValue(data.avg_transaction_volume) ?? 0} L/txn`,
          avgTransactionVolumeRaw: data.avg_transaction_volume,
        });

        setIsSuspended(data.status === "suspended");

        setFuelTypes(
          Array.isArray(data.fuel_types_offered)
            ? data.fuel_types_offered.map((fuel) => fuel.name)
            : [],
        );

        setFuelingTxnData(
          Array.isArray(data.fueling_transactions)
            ? data.fueling_transactions
            : [],
        );

        setFuelDispensedByFuelType({
          labels: data.fuel_dispensed_by_fuel_type?.labels || [],
          total_volume: (
            data.fuel_dispensed_by_fuel_type?.total_volume || []
          ).map((v) => parseFloat(v) || 0),
          total_amount: (
            data.fuel_dispensed_by_fuel_type?.total_amount || []
          ).map((v) => parseFloat(v) || 0),
        });

        setVendorPaymentData(
          Array.isArray(data.vendor_payments)
            ? data.vendor_payments.map((payment, index) => ({
                ...payment,
                key: payment.key ?? payment.id ?? payment.txnId ?? index,
                fileUrl: resolveFileUrl(payment.fileUrl),
              }))
            : [],
        );

        setOperatingHours(
          Array.isArray(data.operating_hours) ? data.operating_hours : [],
        );

        if (Array.isArray(data.documents)) {
          setDocuments(
            data.documents.map((doc) => ({
              key: String(doc.id),
              name: doc.file?.split("/").pop() || doc.category || "-",
              category: doc.category || "-",
              allowedFormats: doc.allowed_formats || [],
              date: formatDate(doc.uploaded_at),
              status: "Pending",
              fileUrl: resolveFileUrl(doc.file),
            })),
          );
        }
      } else {
        message.error(res?.data?.message || "Failed to load vendor.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to load vendor.");
    }
  };

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No vendor selected. Please open this page from the Fuel Vendors list.",
      );
      return;
    }

    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedId]);

  const handleBack = () => {
    if (location.state?.from === "reports-fuel-vendors") {
      navigate("/reports-analytics", {
        state: { activeTab: "Fuel Vendors" },
      });
    } else {
      navigate("/fuel-vendors-admin");
    }
  };
  const handleEditVendor = () =>
    navigate("/edit-vendor-detail", { state: { id: vendor.id } });

  const handleView = (record) => {
    if (!record?.fileUrl) return;
    const lowerName = record.name.toLowerCase();
    if (lowerName.endsWith(".pdf")) {
      window.open(record.fileUrl, "_blank", "noopener,noreferrer");
    } else {
      const link = document.createElement("a");
      link.href = record.fileUrl;
      link.download = record.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleConfirmSuspension = async (enteredReason) => {
    if (!vendor.id || vendor.id === "-") {
      message.error("No vendor selected.");
      return;
    }

    const formData = new FormData();
    formData.append("status", "suspended");

    try {
      const res = await updateVendorDetails(vendor.id, formData);

      if (Math.floor(res?.status / 100) === 2) {
        setIsSuspended(true);
        setReason(enteredReason);
        setIsFuelModalOpen(false);
        notification.success({
          message: res?.data?.message || "Vendor Suspended",
          placement: "topRight",
        });
      } else {
        message.error(res?.data?.message || "Failed to suspend vendor.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to suspend vendor.",
      );
    }
  };

  const handleEnableFueling = async () => {
    if (!vendor.id || vendor.id === "-") {
      message.error("No vendor selected.");
      return;
    }

    const formData = new FormData();
    formData.append("status", "active");

    try {
      const res = await updateVendorDetails(vendor.id, formData);

      if (Math.floor(res?.status / 100) === 2) {
        setIsSuspended(false);
        setReason("");
        notification.success({
          message: res?.data?.message || "Vendor Reactivated",
          placement: "topRight",
        });
      } else {
        message.error(res?.data?.message || "Failed to reactivate vendor.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to reactivate vendor.",
      );
    }
  };

  const handleWiPayPayment = async ({ payAll, transactionIds }) => {
    if (!passedId) return false;

    try {
      const res = await initiateWiPaySettlement(
        passedId,
        payAll ? { pay_all: true } : { transaction_ids: transactionIds },
      );

      if (Math.floor(res?.status / 100) === 2) {
        const checkoutUrl = res?.data?.data?.checkout_url;

        if (checkoutUrl) {
          notification.success({
            message: "Redirecting to WiPay",
            description:
              res?.data?.message || "WiPay checkout initiated successfully",
            placement: "topRight",
          });
          savePaymentReturn(location);
          window.location.href = checkoutUrl;
          return true;
        }

        notification.error({
          message: "WiPay Payment Failed",
          description: "WiPay checkout URL was not returned.",
          placement: "topRight",
        });
        return false;
      }

      notification.error({
        message: "WiPay Payment Failed",
        description: getErrorMessage(
          res?.data,
          "Failed to initiate WiPay payment.",
        ),
        placement: "topRight",
      });
      return false;
    } catch (err) {
      notification.error({
        message: "WiPay Payment Failed",
        description: getErrorMessage(
          err?.response?.data,
          "Failed to initiate WiPay payment.",
        ),
        placement: "topRight",
      });
      return false;
    }
  };

  const handleConfirmPayment = async ({
    payAll,
    transactionIds,
    paymentMethod,
    file,
  }) => {
    if (!passedId) return false;

    try {
      const formData = new FormData();
      formData.append("payment_method", paymentMethod);
      formData.append("proof_file", file);
      if (payAll) {
        formData.append("pay_all", "true");
      } else {
        formData.append("transaction_ids", JSON.stringify(transactionIds));
      }

      const res = await payVendorSettlement(passedId, formData);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message: "Payment Confirmed",
          description: res?.data?.message || "Payment has been submitted.",
          placement: "topRight",
        });
        fetchVendor();
        return true;
      }

      notification.error({
        message: "Payment Failed",
        description: getErrorMessage(res?.data, "Failed to submit payment."),
        placement: "topRight",
      });
      return false;
    } catch (err) {
      notification.error({
        message: "Payment Failed",
        description: getErrorMessage(
          err?.response?.data,
          "Failed to submit payment.",
        ),
        placement: "topRight",
      });
      return false;
    }
  };

  const fuelTypeFilters = [
    ...new Set(fuelingTxnData.map((item) => item.fuelType)),
  ].map((type) => ({
    text: type,
    value: type,
  }));

  const fuelingTxnColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text) => {
        const parts = (text || "").split(" ");
        const datePart = parts[0];
        const timePart = parts[1];

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{datePart}</span>
            <span
              style={{
                color: "#71717A",
              }}
            >
              {timePart}
            </span>
          </div>
        );
      },
    },
    { title: "Txn Id", dataIndex: "txnId", key: "txnId", enableSearch: true },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: true,
      render: (name) => <DriverCell name={name} />,
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: true,
    },
    {
      title: "Mileage",
      dataIndex: "mileage",
      key: "mileage",
      sorter: (a, b) =>
        (parseFloat((a.mileage || "").replace(/,/g, "").replace(" km", "")) ||
          0) -
        (parseFloat((b.mileage || "").replace(/,/g, "").replace(" km", "")) ||
          0),
    },
    {
      title: "Fuel Level",
      dataIndex: "fuelLevel",
      key: "fuelLevel",
      sorter: (a, b) =>
        (parseFloat((a.fuelLevel || "").replace(" L", "")) || 0) -
        (parseFloat((b.fuelLevel || "").replace(" L", "")) || 0),
    },
    {
      title: "Vendors",
      dataIndex: "vendor",
      key: "vendor",
      enableSearch: true,
      render: (name) => <VendorCell name={name} />,
    },
    {
      title: "Station ID",
      dataIndex: "stationId",
      key: "stationId",
      enableSearch: true,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      enableSearch: true,
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: fuelTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
      render: (type) => <span className="fuel-type-badge">{type}</span>,
    },
    {
      title: "Fuel Added",
      dataIndex: "volume",
      key: "volume",
      sorter: (a, b) =>
        (parseFloat((a.volume || "").replace(" L", "")) || 0) -
        (parseFloat((b.volume || "").replace(" L", "")) || 0),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
      render: (v) => `$${Number(v || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Approved", value: "Approved" },
        { text: "Denied", value: "Denied" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span
          className={`status-badge ${status === "Approved" ? "approved" : "denied"}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      filters: [
        { text: "Paid", value: "Paid" },
        { text: "Unpaid", value: "Unpaid" },
      ],
      onFilter: (value, record) => record.paymentStatus === value,
      render: (status) =>
        status === "Paid" || status === "Unpaid" ? (
          <span className={`status-badge ${status.toLowerCase()}`}>
            {status}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Settlement Id",
      dataIndex: "settlementId",
      key: "settlementId",
      enableSearch: true,
      render: (id) => id || "-",
    },
  ];

  const paymentMethodFilters = [
    ...new Set(vendorPaymentData.map((item) => item.paymentMethod)),
  ].map((method) => ({
    text: method,
    value: method,
  }));

  const vendorPaymentColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
      render: (text) => {
        const parts = (text || "").split(" ");
        const datePart = parts[0];
        const timePart = parts[1];

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{datePart}</span>
            <span
              style={{
                color: "#71717A",
              }}
            >
              {timePart}
            </span>
          </div>
        );
      },
    },
    {
      title: "Vendors",
      dataIndex: "vendor",
      key: "vendor",
      enableSearch: true,
      render: (name) => <VendorCell name={name} />,
    },
    {
      title: "Vendor ID",
      dataIndex: "vendorId",
      key: "vendorId",
      enableSearch: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
      render: (v) =>
        `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
    },
    { title: "Txn Id", dataIndex: "txnId", key: "txnId", enableSearch: true },
    {
      title: "Transactions",
      dataIndex: "transactionCount",
      key: "transactionCount",
      sorter: (a, b) => (a.transactionCount || 0) - (b.transactionCount || 0),
      render: (count) => count ?? "-",
    },
    {
      title: "Payment Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      filters: paymentMethodFilters,
      onFilter: (value, record) => record.paymentMethod === value,
      render: (method) => <span className="fuel-type-badge">{method}</span>,
    },
    {
      title: "Paid By",
      dataIndex: "paidBy",
      key: "paidBy",
      enableSearch: true,
      render: (name) => <DriverCell name={name} />,
    },
    {
      title: "Payment Proof",
      dataIndex: "proof",
      key: "proof",
      render: (_, record) =>
        String(record.paymentMethod || "").toLowerCase() === "wipay" ? (
          <Tooltip title="Paid online through WiPay. No proof file is required.">
            <span className="fuel-vendor__wipay-proof">
              <SafetyCertificateOutlined />
              Paid via WiPay
            </span>
          </Tooltip>
        ) : !record.fileUrl ? (
          "-"
        ) : (
        <button
          className="fuel-vendor__proof-link"
          onClick={() => {
            if (record.fileUrl) {
              window.open(record.fileUrl, "_blank", "noopener,noreferrer");
            }
          }}
        >
          <span className="fuel-vendor__proof-icon">
            <DownloadOutlined />
          </span>
          {record.proof}
        </button>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Paid", value: "Paid" },
        { text: "Unpaid", value: "Unpaid" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-badge ${(status || "").toLowerCase()}`}>
          {status}
        </span>
      ),
    },
  ];

  const docColumns = [
    {
      title: "Document Name",
      dataIndex: "name",
      key: "name",
      enableSearch: true,
      render: (name) => {
        const icon = name.toLowerCase().endsWith(".pdf") ? pdfIcon : docIcon;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img src={icon} alt="doc" style={{ width: 20, height: 20 }} />
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      filters: [
        { text: "License", value: "License" },
        { text: "Tax", value: "Tax" },
        { text: "Insurance", value: "Insurance" },
      ],
      onFilter: (value, record) => record.category === value,
      render: (category) => {
        const lower = (category || "").toLowerCase();
        let variant = "default";
        if (lower.includes("license")) variant = "license";
        else if (lower.includes("tax")) variant = "tax";
        else if (lower.includes("insurance")) variant = "insurance";
        else if (lower.includes("registration") || lower.includes("station"))
          variant = "station";

        return (
          <span className={`badge-category badge-${variant}`}>{category}</span>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [
        { text: "Verified", value: "Verified" },
        { text: "Active", value: "Active" },
        { text: "Pending", value: "Pending" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => {
        const cls =
          status === "Verified"
            ? "status-verified"
            : status === "Active"
              ? "status-active"
              : "status-pending";
        return <span className={cls}>{status}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          canEdit && {
            key: "delete",
            label: "Remove",
            style: { color: "#DC2626", fontWeight: 500 },
            icon: <DeleteOutlined />,
            onClick: () => {
              setSelectedRow(record);
              setIsDeleteModalOpen(true);
            },
          },
          {
            key: "view",
            label: "View",
            icon: <EyeOutlined />,
            style: { color: "#000000", fontWeight: 500 },
            onClick: () => handleView(record),
          },
        ].filter(Boolean);
        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                width: 24,
                height: 24,
              }}
            >
              <BsThreeDotsVertical style={{ fontSize: 20, color: "#4F46E5" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  const [documents, setDocuments] = useState([]);

  const fuelDataBar = {
    labels: fuelDispensedByFuelType.labels,
    spend: fuelDispensedByFuelType.total_volume,
  };

  return (
    <div className="fuel-vendor">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>

      <div className="header-wrapper">
        <div className="title">Vendor Detail</div>
        <div className="btns">
          {canEdit && (
            <>
              {!isSuspended ? (
                <Button
                  className="fuel-btn"
                  icon={<LockOutlined />}
                  onClick={() => setIsFuelModalOpen(true)}
                >
                  Suspend
                </Button>
              ) : (
                <Popconfirm
                  title="Are you sure you want to activate this vendor?"
                  onConfirm={handleEnableFueling}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    className="fuel-btn enabled"
                    icon={<LockOutlined />}
                    style={{ color: "#16a34a", backgroundColor: "#16a34a1a" }}
                  >
                    Reactivate
                  </Button>
                </Popconfirm>
              )}
              <Button
                icon={<DollarOutlined />}
                type="primary"
                className="pay-vendor-btn"
                onClick={() => setIsPayModalOpen(true)}
              >
                Pay Vendor
              </Button>
              <Button
                icon={<EditOutlined />}
                type="primary"
                className="edit-btn"
                onClick={handleEditVendor}
              >
                Edit Vendor
              </Button>
            </>
          )}
        </div>
      </div>

      {isSuspended && (
        <div className="suspension-banner">
          <div className="suspension-banner__content">
            <div className="suspension-banner__title">Suspended Reason</div>
            {reason && (
              <div className="suspension-banner__reason">{reason}</div>
            )}
          </div>
        </div>
      )}

      <GenericModal
        open={isFuelModalOpen}
        title="Confirm Vendor Suspension"
        subtitle={`Are you sure you want to suspend the vendor account for ${companyName}? This action will immediately block all fueling activities and decline any active or future card transactions.`}
        highlightText={companyName}
        showReason={true}
        confirmText="Confirm Suspension"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsFuelModalOpen(false)}
        onConfirm={handleConfirmSuspension}
      />

      <PayVendorModal
        open={isPayModalOpen}
        onClose={() => setIsPayModalOpen(false)}
        onConfirm={handleConfirmPayment}
        onWiPay={handleWiPayPayment}
        vendor={vendor}
        vendorId={passedId}
      />

      <div className="row">
        <div className="tabs">
          <GenericStatCard
            title="Gross Sales"
            value={vendor.grossSales}
            {...(vendor.grossSalesRaw?.percentage != null && {
              percentage: `${vendor.grossSalesRaw.percentage}`,
            })}
            isIncrease={vendor.grossSalesRaw?.is_increase}
            subtitle="vs. last month"
            icon={paymentIconSale}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Fees (settlements)"
            value={vendor.feesSettlements}
            subtitle="3.0% effective rate"
            icon={settlementIcon}
          />
        </div>
        <div className="tabs">
          <GenericStatCard
            title="Net Payable"
            value={vendor.netPayable}
            {...(vendor.netPayableRaw?.percentage != null && {
              percentage: `${vendor.netPayableRaw.percentage}`,
            })}
            isIncrease={vendor.netPayableRaw?.is_increase}
            subtitle="vs. last month"
            icon={netPayable}
          />
        </div>
      </div>
      {/* Profile + stat cards */}
      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              {vendor.avatar ? (
                <img
                  src={vendor.avatar}
                  alt={companyName}
                  className="profile-avatar"
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div
                  className="profile-avatar"
                  style={{
                    background: getAvatarColor(companyName),
                    color: "#fff",
                  }}
                >
                  {getInitials(companyName)}
                </div>
              )}
            </div>
            <div className="profile-body">
              <div className="company-name">{companyName}</div>
              {isSuspended && (
                <div className="company-name-badge-row">
                  <span className="badge badge-suspended">Suspended</span>
                </div>
              )}
              <div className="company-id">Vendor / ID: {vendor.id}</div>
              <div
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                {!isSuspended && (
                  <span className="badge badge-active">Active</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="right-column">
          <div className="tabs">
            <GenericStatCard
              title="Total Revenue (YTD)"
              value={vendor.totalRevenueYtd}
              {...(vendor.totalRevenueYtdRaw?.percentage != null && {
                percentage: `${vendor.totalRevenueYtdRaw.percentage}`,
              })}
              isIncrease={vendor.totalRevenueYtdRaw?.is_increase}
              subtitle="vs. last year"
              icon={creditIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Avg. Transaction"
              value={vendor.avgTransactionAmount}
              subtitle="Last 30 days rolling"
              icon={grayPaymentIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Total Sales (MTD)"
              value={vendor.totalSalesMtd}
              {...(vendor.totalSalesMtdRaw?.percentage != null && {
                percentage: `${vendor.totalSalesMtdRaw.percentage}`,
              })}
              isIncrease={vendor.totalSalesMtdRaw?.is_increase}
              subtitle="vs last month"
              icon={paymentIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Liters Dispensed"
              value={vendor.litersDispensed}
              subtitle="Stable"
              icon={humididtyIcon}
            />
          </div>
        </div>
      </div>

      {/* Info card */}
      <div className="row">
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={grayFuelIcon} alt="" />
                </div>
                <div className="info-label">Legal Entity Name</div>
              </div>
              <div className="info-value">{vendor.company}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dateIcon} alt="" />
                </div>
                <div className="info-label">Date Incorporated</div>
              </div>
              <div className="info-value">{vendor.dateIncorporated}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dateIcon} alt="" />
                </div>
                <div className="info-label">Registration Date</div>
              </div>
              <div className="info-value">{vendor.registrationDate}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={regIcon} alt="" />
                </div>
                <div className="info-label">Tax ID / EIN</div>
              </div>
              <div className="info-value">{vendor.taxId}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={currencyIcon} alt="" />
                </div>
                <div className="info-label">Currency</div>
              </div>
              <div className="info-value">{vendor.currency}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={contactIcon} alt="" />
                </div>
                <div className="info-label">Contact Manager</div>
              </div>
              <div className="info-value">{vendor.contactManager}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={mailIcon} alt="" />
                </div>
                <div className="info-label">Contact Email</div>
              </div>
              <div className="info-value">{vendor.contactEmail}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={locIcon} alt="" />
                </div>
                <div className="info-label">Location</div>
              </div>
              <div className="info-value">{vendor.location}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={grayFuelIcon} alt="" />
                </div>
                <div className="info-label">Brand Name</div>
              </div>
              <div className="info-value">{vendor.brandName}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={grayFuelIcon} alt="" />
                </div>
                <div className="info-label">Fuel Brand</div>
              </div>
              <div className="info-value">{vendor.fuelBrand}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={callIcon} alt="" />
                </div>
                <div className="info-label">Contact Phone</div>
              </div>
              <div className="info-value">{vendor.contactPhone}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={vendorTypeIcon} alt="" />
                </div>
                <div className="info-label">Vendor Type</div>
              </div>
              <div className="info-value">{vendor.vendorType}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={storefrontIcon} alt="" />
                </div>
                <div className="info-label">Station Name</div>
              </div>
              <div className="info-value">{vendor.stationName}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={regIcon} alt="" />
                </div>
                <div className="info-label">Station ID</div>
              </div>
              <div className="info-value">{vendor.stationId}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={pinDropIcon} alt="" />
                </div>
                <div className="info-label">Station Address</div>
              </div>
              <div className="info-value">{vendor.stationAddress}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={wipayAccountIcon} alt="" />
                </div>
                <div className="info-label">WiPay Account Number</div>
              </div>
              <div className="info-value">{vendor.wipayAccountNumber}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={wipayApiIcon} alt="" />
                </div>
                <div className="info-label">WiPay API Key</div>
              </div>
              <div className="info-value">{vendor.wipayApiKey}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="fuel-type-card">
        {fuelTypes.length > 0 ? (
          <FuelTypeCard
            fuelList={fuelTypes}
            selectedFuel={fuelTypes}
            disabled={true}
          />
        ) : (
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--stroke-2, #dbe5e5)",
              borderRadius: 16,
              padding: 24,
            }}
          >
            <Empty description="No Fuel Type data available" />
          </div>
        )}
      </div>

      {/* Operating Hours */}
      <div className="operating-hours-card">
        {operatingHours.length > 0 ? (
          <GenericOperatingHours value={operatingHours} disabled={true} />
        ) : (
          <div
            style={{
              background: "var(--white)",
              border: "1px solid var(--stroke-2, #dbe5e5)",
              borderRadius: 16,
              padding: 24,
            }}
          >
            <Empty description="No Operating Hours data available" />
          </div>
        )}
      </div>

      {/* Avg transaction stat */}
      <div className="fuel-transaction-card">
        <GenericStatCard
          title="Average Transaction Volume/Fuel"
          value={vendor.avgTransactionVolume}
          {...(vendor.avgTransactionVolumeRaw?.percentage != null && {
            percentage: `${vendor.avgTransactionVolumeRaw.percentage}`,
          })}
          isIncrease={vendor.avgTransactionVolumeRaw?.is_increase}
          subtitle="vs. last month"
          icon={creditIcon}
        />
      </div>

      {/* Fueling Transactions table */}
      <GenericTable
        title="Fueling Transactions"
        dataSource={fuelingTxnData}
        columns={fuelingTxnColumns}
        enableRowSelection={false}
      />

      {/* Fuel Vendors Payments table */}
      <GenericTable
        title="Fuel Vendors Payments"
        dataSource={vendorPaymentData}
        columns={vendorPaymentColumns}
        enableRowSelection={false}
        expandable={{
          expandedRowRender: (record) => (
            <SettlementTransactionsTable transactions={record.transactions} />
          ),
          rowExpandable: (record) => record.transactions?.length > 0,
        }}
      />

      {/* Bar chart */}
      <div className="graph-container-vendor">
        <div className="graph fueldata-graph-vendor-type">
          <BarChart
            graphData={{
              labels: fuelDataBar.labels,
              spend: fuelDataBar.spend,
            }}
            lengthKeys={1}
            colourTheme={["#EA580C"]}
            graphTitle="Fuel Dispensed by Fuel Type"
            selectTenByDefault={true}
            units={["L"]}
            classNameCSS="quantity-to-use"
            grayzoned={true}
          />
        </div>
      </div>

      <div className="document-table">
        <GenericDocumentManagerTable
          initialDocuments={documents}
          showTableOnly={true}
          hideRemoveAction={true}
        />
      </div>
    </div>
  );
};

export default FuelVendorDetails;
